import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GameRound, IncorrectGuess } from '../types/game';
import PageLayout from '../components/layout/PageLayout';
import './GuessPage.css';
import artistAliases from '../assets/artistAliases.json';
import statsService from '../services/statsService';
import { MOSAIC_TILE_COUNT } from '../constants/game';
import { useGameFilters } from '../hooks/useGameFilters';
import { useMosaicPieces } from '../hooks/useMosaicPieces';
import ImageContainer from '../components/GameImage/ImageContainer';
import ImageProtection from '../components/GameImage/ImageProtection';

const GuessPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const {
        gameRounds,
        artists,
        rounds,
        isRanked,
        scoreMultiplier,
        artistCount,
        isBlurry,
        blurAmount,
        hasTimeLimit,
        timeLimitMs,
        isCropped,
        cropPercentage,
        isFlipped,
        isInverted,
        isMosaic,
        isNoColor,
    } = location.state as {
        gameRounds: GameRound[];
        artists: string[];
        rounds: number;
        isRanked: boolean;
        scoreMultiplier: number;
        artistCount: number;
        isBlurry: boolean;
        blurAmount: number;
        hasTimeLimit: boolean;
        timeLimitMs: number;
        isCropped: boolean;
        cropPercentage: number;
        isFlipped: boolean;
        isInverted: boolean;
        isMosaic: boolean;
        isNoColor: boolean;
    };

    const [currentRound, setCurrentRound] = useState(0);
    const [score, setScore] = useState(0);
    const [guess, setGuess] = useState('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [incorrectGuesses, setIncorrectGuesses] = useState<IncorrectGuess[]>([]);
    const [showImage, setShowImage] = useState(true);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

    // Sound effects
    const correctSoundRef = useRef<HTMLAudioElement | null>(null);
    const incorrectSoundRef = useRef<HTMLAudioElement | null>(null);

    // Initialize sound effects
    useEffect(() => {
        correctSoundRef.current = new Audio('/sounds/correct.mp3');
        incorrectSoundRef.current = new Audio('/sounds/incorrect.mp3');
        
        // Set volume (optional, adjust as needed)
        if (correctSoundRef.current) correctSoundRef.current.volume = 0.5;
        if (incorrectSoundRef.current) incorrectSoundRef.current.volume = 0.5;

        // Handle errors silently if sound files don't exist
        if (correctSoundRef.current) {
            correctSoundRef.current.onerror = () => console.warn('Correct sound file not found');
        }
        if (incorrectSoundRef.current) {
            incorrectSoundRef.current.onerror = () => console.warn('Incorrect sound file not found');
        }
    }, []);

    useEffect(() => {
        if (gameRounds && currentRound + 1 < gameRounds.length) {
            const nextImageUrl = gameRounds[currentRound + 1].post.imageUrl;
            const img = new Image();
            img.src = nextImageUrl;
        }
    }, [currentRound, gameRounds]);

    useEffect(() => {
        // On round change, ensure the image shows and we wait for it to load before starting timer
        setShowImage(true);
        setImageLoaded(false);
        setImageDimensions(null);
    }, [currentRound]);

    useEffect(() => {
        if (!hasTimeLimit) {
            setShowImage(true);
            return;
        }
        if (!imageLoaded) {
            // Do not start timer until image is rendered and loaded
            return;
        }
        const duration = Math.max(0, Number.isFinite(timeLimitMs) ? timeLimitMs : 0);
        const timeoutId = setTimeout(() => setShowImage(false), duration);
        return () => clearTimeout(timeoutId);
    }, [currentRound, hasTimeLimit, timeLimitMs, imageLoaded]);

    const autocompleteOptions = useMemo(() => {
        const aliases = artistAliases.map(item => item.alias);
        return Array.from(new Set([...artists, ...aliases]));
    }, [artists]);

    const handleGuessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const oldValue = guess;
        const newValue = e.target.value;

        if (autocompleteOptions.includes(oldValue) && newValue.length > oldValue.length) {
            return;
        }

        setGuess(newValue);

        if (newValue.length >= 2) {
            const filteredSuggestions = autocompleteOptions.filter(artist =>
                artist.toLowerCase().includes(newValue.toLowerCase())
            );

            if (filteredSuggestions.length === 1 && newValue.length > oldValue.length) {
                setGuess(filteredSuggestions[0]);
                setSuggestions([]);
            } else {
                setSuggestions(filteredSuggestions);
            }
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionClick = (suggestion: string) => {
        setGuess(suggestion);
        setSuggestions([]);
    };

    const handleGuessSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log('--- Guess Submitted ---');

        if (!gameRounds) {
            console.error('`handleGuessSubmit` called but `gameRounds` is not set.');
            return;
        }

        const aliasMap = new Map(artistAliases.map(item => [item.alias.toLowerCase(), item.trueName]));
        const submittedGuess = guess.trim().toLowerCase();
        const userGuess = aliasMap.get(submittedGuess) || submittedGuess;
        const correctAnswer = gameRounds[currentRound].correctAnswer.toLowerCase();
        const isCorrect = userGuess === correctAnswer;
        let updatedIncorrectGuesses = incorrectGuesses;

        // Play sound effect
        if (isCorrect) {
            correctSoundRef.current?.play().catch(err => console.warn('Failed to play correct sound:', err));
        } else {
            incorrectSoundRef.current?.play().catch(err => console.warn('Failed to play incorrect sound:', err));
        }

        if (!isCorrect) {
            const newIncorrectGuess = {
                userGuess: guess.trim(),
                correctAnswer: gameRounds[currentRound].correctAnswer,
                post: gameRounds[currentRound].post,
            };
            updatedIncorrectGuesses = [...incorrectGuesses, newIncorrectGuess];
            setIncorrectGuesses(updatedIncorrectGuesses);
        }

        console.log(`Round: ${currentRound + 1}/${rounds}`);
        console.log(`User's Guess: "${userGuess}"`);
        console.log(`Correct Answer: "${correctAnswer}"`);
        console.log(`Was guess correct? ${isCorrect}`);
        console.log(`Score (Before): ${score}`);

        // Record stats (non-blocking; await to avoid overlapping rounds)
        try {
            await statsService.recordGuess(gameRounds[currentRound].correctAnswer, isCorrect);
        } catch {}

        if (currentRound < rounds - 1) {
            if (isCorrect) {
                setScore(score + 1);
                console.log(`Score (After): ${score + 1}`);
            } else {
                console.log(`Score (After): ${score}`);
            }
            console.log('Advancing to the next round.');
            setCurrentRound(currentRound + 1);
            setGuess('');
        } else {
            const finalScore = isCorrect ? score + 1 : score;
            console.log(`Score (After): ${finalScore}`);
            console.log('Game over.');
            navigate('/results', {
                state: {
                    score: finalScore,
                    rounds,
                    incorrectGuesses: updatedIncorrectGuesses,
                    isRanked,
                    scoreMultiplier,
                    artistCount,
                },
            });
        }
        console.log('----------------------');
        setSuggestions([]);
    };

    const hasRounds = Array.isArray(gameRounds) && gameRounds.length > 0;
    const currentRoundData = hasRounds ? gameRounds[currentRound] : null;

    const filterCss = useGameFilters({
        isBlurry,
        blurAmount,
        isNoColor,
        isInverted,
    });

    const tileOrder = useMosaicPieces({
        isMosaic,
        currentRound,
    });

    if (!hasRounds || !currentRoundData) {
        return <PageLayout><div>Game could not be started or no rounds were generated.</div></PageLayout>;
    }

    return (
        <PageLayout>
            <div>
                <h1>Guess the Artist</h1>
                <p>Round: {currentRound + 1} / {rounds}</p>
                <p>Score: {score}</p>
                <form onSubmit={handleGuessSubmit} className="guess-form">
                    <div className="autocomplete-container">
                        <input
                            type="text"
                            value={guess}
                            onChange={handleGuessChange}
                            placeholder="Enter artist name"
                            autoComplete="off"
                        />
                        {suggestions.length > 0 && (
                            <ul className="suggestions-list">
                                {suggestions.map((suggestion, index) => (
                                    <li key={index} onClick={() => handleSuggestionClick(suggestion)}>
                                        {suggestion}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <button type="submit">Guess</button>
                </form>
                {showImage && currentRoundData && (
                    <ImageProtection>
                        <ImageContainer
                            imageUrl={currentRoundData.post.imageUrl}
                            filterCss={filterCss}
                            isMosaic={isMosaic}
                            difficultySettings={{
                                isBlurry,
                                blurAmount,
                                isCropped,
                                cropPercentage,
                                isFlipped,
                                isInverted,
                                isNoColor,
                            }}
                            tileOrder={tileOrder}
                            imageDimensions={imageDimensions}
                            onImageLoad={(dimensions) => {
                                setImageDimensions(dimensions);
                                setImageLoaded(true);
                            }}
                            onTileLoad={() => setImageLoaded(true)}
                        />
                    </ImageProtection>
                )}
            </div>
        </PageLayout>
    );
};

export default GuessPage; 